import React from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import tokens from "@/theme/tokens";

export type FieldProps = Omit<TextFieldProps, "label"> & {
  /** Rendered above the field in normal document flow, never as MUI's
   * floating label — see the component doc comment below. */
  label?: React.ReactNode;
};

/**
 * Playful's form spec puts a label above its field, 6px away, in normal
 * document flow — not MUI's floating label nested inside the outline. MUI
 * has no theme-level switch for that: `TextField`'s label position and the
 * `OutlinedInput` notch geometry it drives are wired together in the
 * component itself, so the only way to get Playful's placement is to render
 * the label outside `TextField` and never give `TextField` a `label` at all
 * (no label means no notch to cut).
 *
 * This is the one place that happens. Every call site that used to pass
 * `label="…"` to `TextField` — or hand-roll a `Box`/`span` above one — renders
 * this instead. Everything else forwards straight through to `TextField`
 * unchanged: `value`, `onChange`, `error`, `helperText`, `disabled`,
 * `placeholder`, `multiline`, `rows`, `type`, `select` (with its `children`
 * options), `inputRef`, `sx`, and anything else callers pass via `...rest`.
 *
 * `id`/`htmlFor` pairing is generated with `useId` when a caller doesn't
 * supply one — the same pairing `TextField` used to set up itself internally
 * whenever it had a `label`, now done here since `TextField` no longer gets
 * one.
 *
 * `select` mode needs a second pairing, not just the first one. MUI's
 * `TextField select` (non-native) renders a `<div role="combobox">`, not a
 * labelable element — a `<label for>` pointing at it does nothing, which is
 * why `TextField` itself never sets `htmlFor` for select mode (see its
 * source: `htmlFor: select && !nativeSelect ? undefined : id`). Instead it
 * threads a `labelId` into the `Select`'s `aria-labelledby` and renders the
 * label as a plain `<div>` alongside it. Since `TextField` computes that
 * `labelId` from its own `label` prop — which it never receives here — this
 * component has to compute and wire it up itself, or a select field renders
 * with no accessible name at all. Text-mode fields are unaffected: they keep
 * the plain `htmlFor`/`id` pairing above.
 *
 * `ref` forwards to the underlying `TextField`, exactly where it went before
 * this wrapper existed (MUI forwards a `TextField` ref to its root
 * `FormControl`, not into the `<input>` — see `inputRef` for that). This
 * keeps `react-hook-form` `Controller` wiring — `{...field}` spreads
 * including its `ref` — working exactly as it did at the old call sites.
 */
const Field = React.forwardRef<HTMLDivElement, FieldProps>(function Field(
  {
    label,
    id,
    required,
    disabled,
    error,
    fullWidth,
    sx,
    children,
    select = false,
    slotProps,
    ...rest
  },
  ref
) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const labelId = select ? `${inputId}-label` : undefined;

  // Only the non-native Select (MUI's own combobox, the default and the only
  // mode any call site in this app uses) needs the labelId hand-off — a
  // native <select> still supports a plain `htmlFor`, same as a text input.
  const isNativeSelect = Boolean(
    (slotProps as { select?: { native?: boolean } } | undefined)?.select
      ?.native
  );
  const needsLabelId = select && !isNativeSelect;

  const mergedSlotProps = needsLabelId
    ? ({
        ...slotProps,
        select: {
          labelId,
          ...(slotProps as { select?: object } | undefined)?.select,
        },
      } as TextFieldProps["slotProps"])
    : slotProps;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: tokens.space.formLabelGap,
        ...(fullWidth ? { width: "100%" } : null),
      }}
    >
      {label != null && (
        <InputLabel
          htmlFor={needsLabelId ? undefined : inputId}
          id={needsLabelId ? labelId : undefined}
          disabled={disabled}
          error={error}
          shrink
          /**
           * `shrink` stops MUI trying to float the label, and `transform:
           * none` removes the 0.75 scale that goes with it — but that leaves
           * MUI's own label typography behind: ~16px in `text.secondary`,
           * which renders as a grey caption rather than a field label.
           *
           * Playful's UI label is 13/700, which is exactly `label`, in the
           * primary ink. Without this the labels move position and nothing
           * else about the form looks any different.
           */
          sx={{
            position: "static",
            transform: "none",
            typography: "label",
            color: "text.primary",
            "&.Mui-focused": { color: "text.primary" },
            "&.Mui-error": { color: "error.main" },
          }}
          {...(needsLabelId ? { component: "div" as const } : null)}
        >
          {label}
        </InputLabel>
      )}
      <TextField
        ref={ref}
        id={inputId}
        select={select}
        slotProps={mergedSlotProps}
        required={required}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        sx={sx}
        {...rest}
      >
        {children}
      </TextField>
    </Box>
  );
});

Field.displayName = "Field";

export default Field;
